import { Clip, EmotionMood, EmotionProfile } from '../types';

export const EMOTION_PROFILES: Record<EmotionMood, EmotionProfile> = {
  Heroic: {
    name: 'Heroic',
    emoji: '⚔️',
    color: '#FF6B6B',
    glow: 'rgba(255, 107, 107, 0.4)',
    coachingTip: 'Project from your diaphragm! Stand tall, take a deep breath before the climax, and deliver with unyielding conviction.',
    cuePhrases: ['"I won\'t back down!"', '"Stand with me!"', '"This ends now!"']
  },
  Heartfelt: {
    name: 'Heartfelt',
    emoji: '✨',
    color: '#38BDF8',
    glow: 'rgba(56, 189, 248, 0.4)',
    coachingTip: 'Speak softly near the mic. Let a gentle crack in your breath show vulnerability and emotional honesty.',
    cuePhrases: ['"I always believed in you."', '"Don\'t let go..."', '"We made a promise."']
  },
  Fierce: {
    name: 'Fierce',
    emoji: '⚡',
    color: '#A855F7',
    glow: 'rgba(168, 85, 247, 0.4)',
    coachingTip: 'Crisp enunciation, sharp consonants, and relentless forward tempo. Do not hesitate on the pauses.',
    cuePhrases: ['"Did you really think you won?"', '"Target acquired."', '"Too slow!"']
  },
  Whimsical: {
    name: 'Whimsical',
    emoji: '🌟',
    color: '#FBBF24',
    glow: 'rgba(251, 191, 36, 0.4)',
    coachingTip: 'Play with pitch jumps and lilting rhythm! Keep your cheeks raised in a smile to brighten your vocal timbre.',
    cuePhrases: ['"Ooh, what does this button do?"', '"Ta-da! Magic!"', '"Hold onto your hats!"']
  },
  Villainesque: {
    name: 'Villainesque',
    emoji: '🩸',
    color: '#EC4899',
    glow: 'rgba(236, 72, 153, 0.4)',
    coachingTip: 'Slow your cadence down. Relish every word with smooth theatrical control and icy calm confidence.',
    cuePhrases: ['"How deliciously futile."', '"Everything according to plan."', '"Bow down."']
  },
  Mystery: {
    name: 'Mystery',
    emoji: '🔮',
    color: '#06B6D4',
    glow: 'rgba(6, 182, 212, 0.4)',
    coachingTip: 'Lower your register, keep words half-whispered, and leave suspenseful pauses between revelations.',
    cuePhrases: ['"The stars are shifting."', '"Listen closely..."', '"The portal is awakening."']
  }
};

export const SAMPLE_PRESET_CLIPS: Clip[] = [];

import { SoulEntry } from '../types';

export const SAMPLE_ENTRIES: SoulEntry[] = [];

/**
 * Procedural Web Audio synthesizer to generate realistic WAV audio stems (vocals guide track + background music track)
 * Guaranteed 100% offline playback support without third-party audio hosting failures.
 */
export function generateProceduralStems(mood: EmotionMood, durationSeconds: number = 20): { bgmBlob: Blob; vocalsBlob: Blob; bgmUrl: string; vocalsUrl: string } {
  const sampleRate = 44100;
  const numSamples = Math.floor(sampleRate * durationSeconds);
  
  // 1. Generate BGM Track (Chords, Arpeggios, Bass, Percussion)
  const bgmBuffer = new Float32Array(numSamples);
  const chordRoots = mood === 'Heroic' ? [220, 261.63, 329.63, 392] // A minor / C major progression
                   : mood === 'Fierce' ? [146.83, 174.61, 220, 246.94] // D minor aggressive
                   : mood === 'Heartfelt' ? [261.63, 329.63, 392, 440] // C major warm
                   : [196, 246.94, 293.66, 369.99]; // G major celestial

  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const chordIndex = Math.floor((t / 3) % chordRoots.length);
    const rootFreq = chordRoots[chordIndex];

    // Ambient pad & chord harmonies
    const harmonic1 = Math.sin(2 * Math.PI * rootFreq * t) * 0.15;
    const harmonic2 = Math.sin(2 * Math.PI * (rootFreq * 1.5) * t) * 0.1;
    const subBass = Math.sin(2 * Math.PI * (rootFreq * 0.5) * t) * 0.25;

    // Rhythmic pulsing arpeggio
    const arpFreq = rootFreq * [1, 1.25, 1.5, 2][Math.floor((t * 4) % 4)];
    const arpEnv = Math.exp(-((t * 4) % 1) * 3);
    const arp = Math.sin(2 * Math.PI * arpFreq * t) * 0.12 * arpEnv;

    // Soft percussion kick every 0.75s
    const beatPhase = (t % 0.75) / 0.75;
    const kick = Math.sin(2 * Math.PI * (120 - beatPhase * 80) * t) * Math.exp(-beatPhase * 8) * 0.3;

    bgmBuffer[i] = Math.max(-1, Math.min(1, harmonic1 + harmonic2 + subBass + arp + kick));
  }

  // 2. Generate Guide Vocals Track (Simulated dialogue speech frequencies & cadence)
  const vocalsBuffer = new Float32Array(numSamples);
  const phrasePattern = [
    { start: 2.0, end: 5.5, freq: 300 },
    { start: 7.0, end: 11.0, freq: 340 },
    { start: 13.0, end: 18.5, freq: 400 } // Dramatic climax
  ];

  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    let sample = 0;

    for (const phrase of phrasePattern) {
      if (t >= phrase.start && t <= phrase.end) {
        const phraseTime = t - phrase.start;
        // Syllable rhythm modulation (approx 4 syllables per second)
        const syllable = Math.max(0, Math.sin(2 * Math.PI * 4 * phraseTime));
        // Formant-like multi-tone synthesis for vocal guide
        const f0 = phrase.freq + Math.sin(phraseTime * 3) * 20;
        const voice1 = Math.sin(2 * Math.PI * f0 * t);
        const voice2 = Math.sin(2 * Math.PI * (f0 * 2) * t) * 0.4;
        const voice3 = Math.sin(2 * Math.PI * (f0 * 3) * t) * 0.2;
        sample = (voice1 + voice2 + voice3) * syllable * 0.35;
        break;
      }
    }
    vocalsBuffer[i] = Math.max(-1, Math.min(1, sample));
  }

  const bgmBlob = createWavBlob(bgmBuffer, sampleRate);
  const vocalsBlob = createWavBlob(vocalsBuffer, sampleRate);

  return {
    bgmBlob,
    vocalsBlob,
    bgmUrl: URL.createObjectURL(bgmBlob),
    vocalsUrl: URL.createObjectURL(vocalsBlob)
  };
}

/**
 * Converts raw Float32Array PCM audio data to an uncompressed 16-bit PCM WAV Blob
 */
function createWavBlob(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  /* RIFF identifier */
  writeString(view, 0, 'RIFF');
  /* file length */
  view.setUint32(4, 36 + samples.length * 2, true);
  /* RIFF type */
  writeString(view, 8, 'WAVE');
  /* format chunk identifier */
  writeString(view, 12, 'fmt ');
  /* format chunk length */
  view.setUint32(16, 16, true);
  /* sample format (raw PCM) */
  view.setUint16(20, 1, true);
  /* channel count (mono) */
  view.setUint16(22, 1, true);
  /* sample rate */
  view.setUint32(24, sampleRate, true);
  /* byte rate (sample rate * block align) */
  view.setUint32(28, sampleRate * 2, true);
  /* block align (channel count * bytes per sample) */
  view.setUint16(32, 2, true);
  /* bits per sample */
  view.setUint16(34, 16, true);
  /* data chunk identifier */
  writeString(view, 36, 'data');
  /* data chunk length */
  view.setUint32(40, samples.length * 2, true);

  // Write PCM audio samples
  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([view], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
